import {
	ComponentType,
	FC,
	PropsWithChildren,
	useCallback,
	useMemo,
	useState,
} from 'react';
import { Theme, ThemeProvider } from '@mui/material/styles';

import { PassengerBookingView } from '@mopla/data-models';
import {
	checkIsBookingCancelled,
	checkIsPassedBooking,
	isLBTItinerary,
	wrapDisplayName,
} from '@mopla/utils';

import { CancellationFlow } from './cancellation/CancellationFlow';
import { CancellationFlowV2 } from './cancellation/CancellationFlowV2';
import { RemoveBookingFlow } from './remove/RemoveBookingFlow';
import {
	BookingCancellationContextProvider,
	IBookingCancellationContext,
} from './context';
import { TFlowType } from './types';

interface IBookingCancellationFlowProps extends PropsWithChildren {
	isNewCancellationDesign?: boolean;
	designTheme?: Theme;
}

export const BookingCancellationFlow: FC<IBookingCancellationFlowProps> = ({
	children,
	isNewCancellationDesign,
	designTheme,
}) => {
	const [bookedTicket, setBookedTicket] = useState<PassengerBookingView | null>(
		null
	);

	/** Initiates the cancellation flow */
	const cancelBooking = useCallback((bookedTicket: PassengerBookingView) => {
		setBookedTicket(bookedTicket);
	}, []);

	const closeHandler = useCallback(() => {
		setBookedTicket(null);
	}, []);

	const flowType = useMemo<TFlowType | null>(() => {
		if (!bookedTicket) return null;

		const isCancelledRide =
			bookedTicket && checkIsBookingCancelled(bookedTicket);
		const isPassedRide = bookedTicket && checkIsPassedBooking(bookedTicket);
		const isLBT = isLBTItinerary(bookedTicket?.itinerary);

		return isCancelledRide || isPassedRide || isLBT ? 'remove' : 'cancel';
	}, [bookedTicket]);

	const ctx = useMemo<IBookingCancellationContext>(
		() => ({ cancelBooking }),
		[cancelBooking]
	);

	return (
		<BookingCancellationContextProvider value={ctx}>
			{children}
			{flowType === 'cancel' && !isNewCancellationDesign && (
				<CancellationFlow
					bookedItinerary={bookedTicket as PassengerBookingView}
					onClose={closeHandler}
				/>
			)}
			{flowType === 'cancel' && isNewCancellationDesign && (
				<ThemeProvider theme={designTheme ?? ((outerTheme) => outerTheme)}>
					<CancellationFlowV2
						bookedItinerary={bookedTicket as PassengerBookingView}
						onClose={closeHandler}
					/>
				</ThemeProvider>
			)}
			{flowType === 'remove' && (
				<RemoveBookingFlow
					bookedItinerary={bookedTicket as PassengerBookingView}
					onClose={closeHandler}
				/>
			)}
		</BookingCancellationContextProvider>
	);
};

export const withBookingCancellationFlow = (
	WrappedComponent: ComponentType
) => {
	const Wrapper: FC = () => {
		return (
			<BookingCancellationFlow>
				<WrappedComponent />
			</BookingCancellationFlow>
		);
	};

	Wrapper.displayName = wrapDisplayName(
		WrappedComponent,
		'withBookingCancellationFlow'
	);

	return Wrapper;
};
